// SPDX-License-Identifier: AGPL-3.0-or-later

import {LongPressable} from '@app/features/app/components/LongPressable';
import channelItemStyles from '@app/features/app/components/layout/ChannelItem.module.css';
import channelItemSurfaceStyles from '@app/features/app/components/layout/ChannelItemSurface.module.css';
import {DropIndicator} from '@app/features/app/components/layout/DropIndicator';
import type {ScrollIndicatorSeverity} from '@app/features/app/components/layout/ScrollIndicatorOverlay';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import {CaretDownIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import React from 'react';

interface GenericChannelItemProps {
	icon?: React.ReactNode;
	name?: string;
	actions?: React.ReactNode;
	badges?: React.ReactNode;
	isSelected?: boolean;
	isMuted?: boolean;
	isDragging?: boolean;
	isOver?: boolean;
	dropIndicator?: {position: 'top' | 'bottom'; isValid: boolean} | null;
	onClick?: () => void;
	onContextMenu?: (event: React.MouseEvent) => void;
	onKeyDown?: (event: React.KeyboardEvent) => void;
	onFocus?: () => void;
	onBlur?: () => void;
	onLongPress?: () => void;
	innerRef?: React.Ref<HTMLDivElement>;
	className?: string;
	pressedClassName?: string;
	containerClassName?: string;
	style?: React.CSSProperties;
	isCategory?: boolean;
	isCollapsed?: boolean;
	onToggle?: () => void;
	disabled?: boolean;
	role?: string;
	tabIndex?: number;
	children?: React.ReactNode;
	extraContent?: React.ReactNode;
	'aria-label'?: string;
	'aria-current'?: React.AriaAttributes['aria-current'];
	'aria-controls'?: string;
	'aria-describedby'?: string;
	'aria-expanded'?: boolean;
	'data-dnd-name'?: string;
	dataScrollIndicator?: ScrollIndicatorSeverity;
	dataScrollId?: string;
	onMouseEnter?: (event: React.MouseEvent) => void;
	onMouseLeave?: (event: React.MouseEvent) => void;
}
export const GenericChannelItem = React.forwardRef<HTMLDivElement, GenericChannelItemProps>(
	(
		{
			icon,
			name,
			actions,
			badges,
			isSelected,
			isOver,
			dropIndicator,
			onClick,
			onContextMenu,
			onKeyDown,
			onFocus,
			onBlur,
			onLongPress,
			innerRef,
			className,
			pressedClassName,
			containerClassName,
			style,
			isCategory,
			isCollapsed,
			disabled,
			role = 'button',
			tabIndex = 0,
			children,
			extraContent,
			'aria-label': ariaLabel,
			'aria-current': ariaCurrent,
			'aria-controls': ariaControls,
			'aria-describedby': ariaDescribedBy,
			'aria-expanded': ariaExpanded,
			'data-dnd-name': dataDndName,
			dataScrollIndicator,
			dataScrollId,
			onMouseEnter,
			onMouseLeave,
		},
		ref,
	) => {
		return (
			<div className={containerClassName} style={{position: 'relative', ...style}} ref={ref}>
				{extraContent}
				{isOver && dropIndicator && <DropIndicator position={dropIndicator.position} isValid={dropIndicator.isValid} />}
				<FocusRing offset={-2} ringClassName={channelItemSurfaceStyles.channelItemFocusRing}>
					<LongPressable
						ref={innerRef}
						disabled={disabled}
						className={clsx(
							channelItemSurfaceStyles.channelItemSurface,
							isSelected && channelItemSurfaceStyles.channelItemSurfaceSelected,
							className,
						)}
						pressedClassName={pressedClassName ?? channelItemStyles.channelItemPressed}
						onClick={onClick}
						onContextMenu={onContextMenu}
						onKeyDown={onKeyDown}
						onFocus={onFocus}
						onBlur={onBlur}
						onMouseEnter={onMouseEnter}
						onMouseLeave={onMouseLeave}
						role={role}
						tabIndex={tabIndex}
						onLongPress={onLongPress}
						aria-label={ariaLabel}
						aria-current={ariaCurrent}
						aria-controls={ariaControls}
						aria-describedby={ariaDescribedBy}
						aria-expanded={ariaExpanded}
						data-channel-list-focus-item="true"
						data-dnd-name={dataDndName}
						data-scroll-indicator={dataScrollIndicator}
						data-scroll-id={dataScrollId}
					>
						{children ? (
							children
						) : (
							<>
								{isCategory ? (
									<div className={channelItemStyles.categoryContainer}>
										<span className={channelItemStyles.categoryName}>{name}</span>
										<CaretDownIcon weight="bold" style={{transform: `rotate(${isCollapsed ? -90 : 0}deg)`}} />
									</div>
								) : (
									<>
										{icon && <div className={channelItemStyles.iconContainer}>{icon}</div>}
										<span className={channelItemStyles.channelName}>{name}</span>
									</>
								)}
								<div className={channelItemStyles.actionsContainer}>
									{actions}
									{badges}
								</div>
							</>
						)}
					</LongPressable>
				</FocusRing>
			</div>
		);
	},
);
GenericChannelItem.displayName = 'GenericChannelItem';
